import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertCircle, Plus, Trash2, X, Building2, Mail, Phone, MapPin, Landmark, CreditCard
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BANK_TYPES = ['Net Banking', 'UPI'];

export const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Bank detail form
  const [showBankForm, setShowBankForm] = useState(false);
  const [bankType, setBankType] = useState('Net Banking');
  const [bankForm, setBankForm] = useState({ bank_name: '', branch_name: '', account_no: '', ifsc_code: '', upi_id: '' });
  const [bankSubmitting, setBankSubmitting] = useState(false);
  const [bankError, setBankError] = useState('');
  const [deleteBankConfirm, setDeleteBankConfirm] = useState(null);

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchCompany = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/companies/${id}`, getAuthHeaders());
      setCompany(res.data);
    } catch (err) { setError('Failed to load company'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCompany(); }, [id]);

  const openBankForm = () => {
    setBankType('Net Banking');
    setBankForm({ bank_name: '', branch_name: '', account_no: '', ifsc_code: '', upi_id: '' });
    setBankError('');
    setShowBankForm(true);
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    setBankError('');
    if (bankType === 'Net Banking' && !bankForm.bank_name.trim()) { setBankError('Bank Name is required'); return; }
    if (bankType === 'UPI' && !bankForm.upi_id.trim()) { setBankError('UPI ID is required'); return; }

    setBankSubmitting(true);
    try {
      const newEntry = {
        id: crypto.randomUUID(),
        type: bankType,
        ...(bankType === 'Net Banking' ? {
          bank_name: bankForm.bank_name || null,
          branch_name: bankForm.branch_name || null,
          account_no: bankForm.account_no || null,
          ifsc_code: bankForm.ifsc_code || null,
          upi_id: null,
        } : {
          bank_name: null, branch_name: null, account_no: null, ifsc_code: null,
          upi_id: bankForm.upi_id || null,
        }),
      };
      const updatedList = [...(company.bank_details || []), newEntry];
      await axios.put(`${BACKEND_URL}/api/companies/${id}`, { bank_details: updatedList }, getAuthHeaders());
      setShowBankForm(false);
      fetchCompany();
    } catch (err) { setBankError(err.response?.data?.detail || 'Failed to add bank detail'); }
    finally { setBankSubmitting(false); }
  };

  const handleDeleteBank = async (entryId) => {
    try {
      const updatedList = (company.bank_details || []).filter(bd => bd.id !== entryId);
      await axios.put(`${BACKEND_URL}/api/companies/${id}`, { bank_details: updatedList }, getAuthHeaders());
      setDeleteBankConfirm(null);
      fetchCompany();
    } catch (err) { setError('Failed to delete bank detail'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (error && !company) return (
    <div>
      <button onClick={() => navigate('/admin/master-data/companies')} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"><ArrowLeft className="h-4 w-4" /> Back to Companies</button>
      <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm"><AlertCircle className="h-5 w-5" />{error}</div>
    </div>
  );
  if (!company) return null;

  const bankDetails = company.bank_details || [];

  return (
    <div data-testid="company-detail-page">
      {/* Header */}
      <div className="mb-8">
        <button onClick={() => navigate('/admin/master-data/companies')} data-testid="back-btn"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-3 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Companies
        </button>
        <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Company Details</h1>
      </div>

      {/* Company Info */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="flex items-start gap-4 mb-6">
          {company.logo ? (
            <img src={company.logo} alt={company.company_name} className="w-16 h-16 object-contain rounded border border-slate-200" />
          ) : (
            <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center"><Building2 className="h-8 w-8 text-slate-400" /></div>
          )}
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{company.company_name}</h2>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${company.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {company.active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
            <div><span className="block text-xs text-slate-500 uppercase mb-0.5">Email</span><span className="text-sm text-slate-900">{company.email || '-'}</span></div>
          </div>
          <div className="flex items-start gap-3">
            <Phone className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
            <div><span className="block text-xs text-slate-500 uppercase mb-0.5">Phone</span><span className="text-sm text-slate-900">{company.phone || '-'}</span></div>
          </div>
          <div className="flex items-start gap-3">
            <CreditCard className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
            <div><span className="block text-xs text-slate-500 uppercase mb-0.5">GST Number</span><span className="text-sm text-slate-900">{company.gst_number || '-'}</span></div>
          </div>
        </div>

        {(company.address || company.city || company.state) && (
          <div className="mt-6 pt-6 border-t border-slate-100">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
              <div>
                <span className="block text-xs text-slate-500 uppercase mb-0.5">Address</span>
                <span className="text-sm text-slate-900">
                  {[company.address, company.area, company.city, company.state, company.pincode, company.country].filter(Boolean).join(', ')}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ========== BANK DETAILS SECTION ========== */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Bank Details</h2>
          {!showBankForm && (
            <button onClick={openBankForm} data-testid="add-bank-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-5 py-2.5 rounded-none hover:bg-orange-600 transition-colors text-sm">
              <Plus className="h-4 w-4" /> Add Bank Detail
            </button>
          )}
        </div>

        {/* Bank Form */}
        {showBankForm && (
          <div className="bg-white rounded-sm border border-slate-200 mb-6">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Add Bank Detail</h3>
              <button onClick={() => setShowBankForm(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleBankSubmit} className="p-6">
              {bankError && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4"><AlertCircle className="h-5 w-5" /><span>{bankError}</span></div>}

              {/* Type selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">Type</label>
                <div className="flex gap-3">
                  {BANK_TYPES.map(t => (
                    <button key={t} type="button" onClick={() => setBankType(t)} data-testid={`bank-type-${t.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`px-4 py-2 border rounded-sm text-sm font-medium transition-colors ${
                        bankType === t ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300'
                      }`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {bankType === 'Net Banking' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Bank Name <span className="text-red-500">*</span></label>
                    <input type="text" value={bankForm.bank_name} onChange={e => setBankForm(p => ({ ...p, bank_name: e.target.value }))}
                      data-testid="bank-input-bank-name"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Branch Name</label>
                    <input type="text" value={bankForm.branch_name} onChange={e => setBankForm(p => ({ ...p, branch_name: e.target.value }))}
                      data-testid="bank-input-branch-name"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Account No</label>
                    <input type="text" value={bankForm.account_no} onChange={e => setBankForm(p => ({ ...p, account_no: e.target.value }))}
                      data-testid="bank-input-account-no"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">IFSC Code</label>
                    <input type="text" value={bankForm.ifsc_code} onChange={e => setBankForm(p => ({ ...p, ifsc_code: e.target.value }))}
                      data-testid="bank-input-ifsc-code"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">UPI ID <span className="text-red-500">*</span></label>
                  <input type="text" value={bankForm.upi_id} onChange={e => setBankForm(p => ({ ...p, upi_id: e.target.value }))}
                    data-testid="bank-input-upi-id" placeholder="e.g. yourname@upi"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
              )}

              <div className="flex gap-3 justify-end pt-6 mt-4 border-t border-slate-200">
                <button type="button" onClick={() => setShowBankForm(false)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={bankSubmitting} data-testid="submit-bank-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {bankSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Add
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Bank Details List */}
        {bankDetails.length === 0 && !showBankForm ? (
          <div className="bg-white rounded-sm border border-slate-200 text-center py-12">
            <Landmark className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500 mb-4">No bank details added yet</p>
            <button onClick={openBankForm} className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium text-sm">
              <Plus className="h-4 w-4" /> Add First Bank Detail
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {bankDetails.map((bd, idx) => (
              <div key={bd.id || idx} className="bg-white rounded-sm border border-slate-200 px-6 py-4" data-testid={`bank-card-${bd.id || idx}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${
                      bd.type === 'UPI' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>{bd.type}</span>
                  </div>
                  <button onClick={() => setDeleteBankConfirm(bd.id || idx)} data-testid={`delete-bank-${bd.id || idx}`}
                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {bd.type === 'UPI' ? (
                  <div className="text-sm">
                    <span className="text-xs text-slate-500 uppercase">UPI ID</span>
                    <p className="font-medium text-slate-900">{bd.upi_id || '-'}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div><span className="block text-xs text-slate-500 uppercase">Bank Name</span><span className="font-medium text-slate-900">{bd.bank_name || '-'}</span></div>
                    <div><span className="block text-xs text-slate-500 uppercase">Branch</span><span className="text-slate-900">{bd.branch_name || '-'}</span></div>
                    <div><span className="block text-xs text-slate-500 uppercase">Account No</span><span className="text-slate-900 font-mono">{bd.account_no || '-'}</span></div>
                    <div><span className="block text-xs text-slate-500 uppercase">IFSC Code</span><span className="text-slate-900 font-mono">{bd.ifsc_code || '-'}</span></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Bank Confirm */}
      {deleteBankConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to remove this bank detail?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteBankConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              <button onClick={() => handleDeleteBank(deleteBankConfirm)} data-testid="confirm-delete-bank-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
